import {
  DialogProvider,
  Separator,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
  ToastProvider,
  useIsMobile,
} from '@bpinternal/ui-kit-next'
import { useSuspenseQuery, type QueryClient } from '@tanstack/react-query'
import { createRootRouteWithContext, Outlet, useParams } from '@tanstack/react-router'
import { useEffect } from 'react'
import { RemoveScroll } from 'react-remove-scroll'
import { FileRouteTypes } from '~/routeTree.gen'
import { cn } from '~/utils'
import { AppSidebar, Boundary, NavbarBreadcrumbs } from '../components'
import { HeaderSettings } from '../components/navigation/HeaderSettings'
import { useCurrentRouteId } from '../hooks/useCurrentRouteId'
import { trackPage, usePortal } from '../providers'
import { DeploymentToastProvider } from '../providers/DeploymentToastProvider'
import { workspacesQueryOptions } from '../queries'
import { useAppStore, useLocalStore } from '../stores'

const FULL_SCREEN_ROUTES: FileRouteTypes['fullPaths'][] = [
  '/welcome',
  '/workspaces/$workspaceId/bots/$botId/new-bot',
  '/workspaces/$workspaceId/bots/$botId/new-chat',
  '/cli-login',
]

type RouterContext = {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async () => {
    const { currentUser: user } = useAppStore.getState()
    if (!user) {
      throw new Error('Unable to get current user')
    }
    return { user }
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(workspacesQueryOptions()),
  component: RootComponent,
})

function RootComponent() {
  const { data: workspaces } = useSuspenseQuery(workspacesQueryOptions())
  const routeId = useCurrentRouteId()
  const expandedSidebar = useLocalStore((state) => state.expandedSidebar)
  const setExpandedSidebar = useLocalStore((state) => state.setExpandedSidebar)
  const localTheme = useLocalStore((s) => s.theme)
  const theme: 'system' | 'light' | 'dark' = localTheme === 'auto' ? 'system' : localTheme
  const isMobile = useIsMobile()

  const { setBannerPortalRef } = usePortal()

  const { workspaceId, botId, conversationId, ...rest } = useParams({ strict: false })

  useEffect(() => {
    if (routeId) {
      trackPage({ name: routeId, workspaceId, botId, conversationId })
    }
  }, [routeId])

  const isFullScreen = FULL_SCREEN_ROUTES.includes(routeId ?? '')

  return (
    <>
      <DialogProvider />
      <ToastProvider position="top-center" richColors theme={theme} />
      {isFullScreen ? (
        <div className="h-[100dvh] w-full">
          <div className="has-[:not(.hidden)]:mb-4" ref={setBannerPortalRef} />
          <Outlet />
        </div>
      ) : (
        <SidebarProvider open={expandedSidebar} onOpenChange={setExpandedSidebar}>
          <DeploymentToastProvider>
            <AppSidebar workspaces={workspaces} />
            <SidebarInset
              className={cn(
                expandedSidebar
                  ? 'max-w-[calc(100%-var(--sidebar-width)-8px)]'
                  : 'max-w-[calc(100%-var(--sidebar-width-icon)-1rem-8px)]',
                'max-lg:max-w-full'
              )}
            >
              <header
                className={cn(
                  'fixed right-0 top-0 z-10 h-16 bg-background transition-[left] duration-200',
                  RemoveScroll.classNames.zeroRight
                )}
                style={{
                  left: isMobile
                    ? '0px'
                    : expandedSidebar
                      ? 'calc(var(--sidebar-width) + 8px)'
                      : 'calc(var(--sidebar-width-icon) + 1rem + 8px)',
                }}
              >
                <div className="flex h-full w-full items-center justify-between gap-10 border-b border-b-border bg-background px-3 sm:px-4 md:px-6">
                  <div className="flex min-w-0 max-w-full items-center gap-1 sm:gap-2">
                    <SidebarTrigger className="-ml-1" />
                    <Separator orientation="vertical" className="mr-1 h-4 sm:mr-2" />
                    <NavbarBreadcrumbs botId={botId} workspaceId={workspaceId} {...rest} />
                  </div>
                  <Boundary loader={null}>
                    <HeaderSettings workspaces={workspaces} workspaceId={workspaceId} />
                  </Boundary>
                </div>
              </header>
              <div className="mt-16 flex-1 p-5 md:p-4 lg:px-6">
                <div className="mx-auto max-w-screen-2xl">
                  <div className="has-[:not(.hidden)]:mb-4" ref={setBannerPortalRef}></div>
                </div>
                <Outlet />
              </div>
            </SidebarInset>
          </DeploymentToastProvider>
        </SidebarProvider>
      )}
    </>
  )
}
